// src/finance/components/ExpenseListEditor.tsx
// Liste de dépenses contrôlée, réutilisée par le calculateur (récurrentes)
// et le suivi mensuel (lignes réelles). Aucun fetch ici : le parent persiste.
import React, { useState } from 'react'
import type { ExpensePeriodicity } from '../../lib/types'
import { fmtEur, parseAmount } from '../format'
import { monthlyAmountOf } from '../expenseMath'

export type EditableExpenseLine = {
  key: string
  label: string
  /** Montant SAISI : mensuel pour /mois, ANNUEL pour /an (lissé /12 dans les totaux). */
  amount: number
  active: boolean
  periodicity: ExpensePeriodicity
  /** Mois d'échéance 1-12 d'une /an (null = non datée). Sans objet pour /mois. */
  dueMonth: number | null
}

const MONTH_LABELS = [
  'janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
  'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.',
] as const

/** Échéance par défaut d'une /an — miroir de `prefillForMonth`, qui fait échoir en
 *  janvier les lignes héritées restées sans mois. */
const DEFAULT_DUE_MONTH = 1

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
}

const inputStyle: React.CSSProperties = {
  background: 'var(--bg)',
  border: '1px solid var(--line)',
  borderRadius: '6px',
  color: 'var(--fg)',
  fontSize: '0.8125rem',
  fontFamily: 'inherit',
  padding: '0.375rem 0.5rem',
}

export function ExpenseListEditor({
  title,
  lines,
  onChange,
  showActiveToggle,
  showPeriodicity = false,
}: {
  title: string
  lines: EditableExpenseLine[]
  onChange: (lines: EditableExpenseLine[]) => void
  showActiveToggle: boolean
  /** Affiche le toggle « /mois | /an » + le mois d'échéance (récurrentes uniquement). */
  showPeriodicity?: boolean | undefined
}) {
  // Brouillon d'affichage par ligne, le temps de la frappe du montant. Le modèle
  // reste piloté par le parent, mais `parseAmount('25,')` vaut 25 : sans ce texte
  // local, un input contrôlé par String(line.amount) avalerait le séparateur et
  // « 25,50 » se transformerait en 2550. Le brouillon est jeté au blur.
  const [drafts, setDrafts] = useState<Record<string, string>>({})

  const clearDraft = (key: string) => {
    setDrafts((prev) => {
      if (!(key in prev)) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Total MENSUALISÉ : les lignes /an comptent pour un douzième (spec dépenses annuelles).
  const total = lines.filter((l) => l.active).reduce((sum, l) => sum + monthlyAmountOf(l), 0)

  const patch = (key: string, changes: Partial<EditableExpenseLine>) => {
    onChange(lines.map((l) => (l.key === key ? { ...l, ...changes } : l)))
  }

  return (
    <section
      style={{
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: '10px',
        padding: '1rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
        <h2 style={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--fg)', margin: 0 }}>
          {title}
        </h2>
        <span data-testid="expense-total" style={{ fontSize: '0.8125rem', color: 'var(--muted)', fontFamily: 'var(--mono)' }}>
          {fmtEur(total)}<span style={{ color: 'var(--dim)' }}> /mois</span>
        </span>
      </div>

      {lines.map((line) => (
        <div key={line.key} style={rowStyle}>
          {showActiveToggle && (
            <input
              type="checkbox"
              checked={line.active}
              onChange={(e) => patch(line.key, { active: e.target.checked })}
              aria-label={`Activer ${line.label || 'la dépense'}`}
            />
          )}
          <input
            style={{ ...inputStyle, flex: 1, opacity: line.active ? 1 : 0.5 }}
            value={line.label}
            placeholder="Libellé"
            onChange={(e) => patch(line.key, { label: e.target.value })}
          />
          <input
            style={{ ...inputStyle, width: '90px', textAlign: 'right', opacity: line.active ? 1 : 0.5 }}
            value={drafts[line.key] ?? String(line.amount)}
            inputMode="decimal"
            aria-label={`Montant de ${line.label || 'la dépense'}`}
            onChange={(e) => {
              const text = e.target.value
              setDrafts((prev) => ({ ...prev, [line.key]: text }))
              const parsed = parseAmount(text)
              if (parsed !== null) patch(line.key, { amount: parsed })
              else if (text.trim() === '') patch(line.key, { amount: 0 })
            }}
            onBlur={() => clearDraft(line.key)}
          />
          {showPeriodicity && (
          <button
            type="button"
            onClick={() =>
              patch(
                line.key,
                line.periodicity === 'monthly'
                  // Un mois d'échéance est OBLIGATOIRE dès qu'une ligne passe en /an :
                  // une /an non datée pèse dans l'objectif lissé (/12) sans jamais être
                  // préremplie dans un mois réel. Janvier par défaut, modifiable.
                  ? { periodicity: 'yearly', dueMonth: DEFAULT_DUE_MONTH }
                  : { periodicity: 'monthly', dueMonth: null },
              )
            }
            aria-label={`Périodicité de ${line.label || 'la dépense'}`}
            title="Basculer mensuel / annuel"
            style={{
              background: 'transparent',
              border: '1px solid var(--line)',
              borderRadius: '5px',
              color: line.periodicity === 'yearly' ? 'var(--fg)' : 'var(--dim)',
              cursor: 'pointer',
              fontSize: '0.6875rem',
              fontFamily: 'inherit',
              padding: '0.25rem 0.375rem',
              whiteSpace: 'nowrap',
              opacity: line.active ? 1 : 0.5,
            }}
          >
            {line.periodicity === 'yearly' ? '/an' : '/mois'}
          </button>
          )}
          {showPeriodicity && line.periodicity === 'yearly' && (
            <select
              aria-label={`Prélevée en (${line.label || 'la dépense'})`}
              // Plus d'option vide : une /an a toujours une échéance. Une ligne héritée
              // restée sans mois s'affiche sur janvier — ce qui est exactement ce que
              // `prefillForMonth` en fait, l'écran et le préremplissage ne divergent pas.
              value={String(line.dueMonth ?? DEFAULT_DUE_MONTH)}
              onChange={(e) => patch(line.key, { dueMonth: Number(e.target.value) })}
              style={{
                ...inputStyle,
                width: '76px',
                padding: '0.25rem 0.25rem',
                fontSize: '0.6875rem',
                opacity: line.active ? 1 : 0.5,
              }}
            >
              {MONTH_LABELS.map((label, i) => (
                <option key={label} value={i + 1}>
                  {label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            onClick={() => {
              clearDraft(line.key)
              onChange(lines.filter((l) => l.key !== line.key))
            }}
            aria-label="Supprimer"
            title="Supprimer"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--dim)',
              cursor: 'pointer',
              fontSize: '1rem',
              padding: '0 0.25rem',
              fontFamily: 'inherit',
            }}
          >
            ×
          </button>
        </div>
      ))}

      <button
        type="button"
        onClick={() =>
          onChange([
            ...lines,
            { key: crypto.randomUUID(), label: '', amount: 0, active: true, periodicity: 'monthly', dueMonth: null },
          ])
        }
        style={{
          alignSelf: 'flex-start',
          background: 'var(--elevated)',
          border: '1px solid var(--line)',
          borderRadius: '6px',
          color: 'var(--muted)',
          fontSize: '0.8125rem',
          fontFamily: 'inherit',
          padding: '0.375rem 0.75rem',
          cursor: 'pointer',
        }}
      >
        Ajouter une dépense
      </button>
    </section>
  )
}
